/**
 * `/admin` → **Announcements**: write what everyone sees, and choose which are on.
 *
 * Three columns, left to right: every announcement with its state and an on/off
 * switch; the one being written; and a live preview of exactly what people will
 * get - the strip under the header and the pop-up card, rendered by the same
 * code that renders them in the app (`features/announcements/announcements.ts`).
 * **Preview full screen** opens the real pop-up over this page, blur and all,
 * and records nothing.
 *
 * The styles are the ones chosen from the announcements sketch: a banner is the
 * strip, and a pop-up is Classic, Schedule or Release notes. Release notes are
 * also kept for "What's new" in the account menu after they stop popping up.
 *
 * `announcements:write` gates the section here and every route on the server,
 * and the table's trigger asks for it again (migration `0018`). Saving, switching
 * and deleting are Activity events.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { LightElement } from '../../lit-base.ts';
import { StoreController } from '../../state/controller.ts';
import { canDo, catalogAvailabilityKnown, catalogAvailable, myPermissions } from '../../state/store.ts';
import {
  createAnnouncement,
  deleteAnnouncement,
  listAdminAnnouncements,
  listAnnouncementReceipts,
  setAnnouncementActive,
  showAnnouncementAgain,
  updateAnnouncement,
} from '../../api/endpoints.ts';
import type {
  Announcement,
  AnnouncementInput,
  AnnouncementPopupStyle,
  AnnouncementReceipt,
  AnnouncementTone,
} from '../../api/types.ts';
import { openAnnouncement, refreshAnnouncements } from '../../state/announcements.ts';
import { renderPopupCard, toneIcon } from '../announcements/announcements.ts';
import {
  announcementStatus,
  eventWindow,
  fromUtcInput,
  STATUS_LABEL,
  toUtcInput,
} from '../announcements/format.ts';
import { confirmAction } from '../../ui/confirm-dialog.ts';
import { showToast } from '../../ui/toast.ts';
import { userMessage } from '../../api/errors.ts';
import { timeAgo } from '../../utils/dates.ts';

const PERMISSION = 'announcements:write';

const POPUP_STYLES: ReadonlyArray<{ id: AnnouncementPopupStyle; label: string; hint: string }> = [
  { id: 'none', label: 'No pop-up', hint: 'Banner only' },
  { id: 'classic', label: 'Classic', hint: 'A message and one button' },
  { id: 'schedule', label: 'Schedule', hint: 'Time window and countdown' },
  { id: 'release_notes', label: 'Release notes', hint: "What's new, kept to reread" },
];

const TONES: ReadonlyArray<{ id: AnnouncementTone; label: string }> = [
  { id: 'info', label: 'Info' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'critical', label: 'Critical' },
];

/** What the form edits: the input, with times as `datetime-local` strings in UTC. */
interface Draft {
  id: string | null;
  title: string;
  body: string;
  tone: AnnouncementTone;
  show_banner: boolean;
  popup_style: AnnouncementPopupStyle;
  version_label: string;
  highlights: string;
  event_starts_at: string;
  event_ends_at: string;
  show_from: string;
  show_until: string;
  audience: 'everyone' | 'admins';
  is_active: boolean;
  dismissible: boolean;
  popup_once: boolean;
}

function blankDraft(): Draft {
  return {
    id: null, title: '', body: '', tone: 'info', show_banner: true, popup_style: 'none',
    version_label: '', highlights: '', event_starts_at: '', event_ends_at: '', show_from: '',
    show_until: '', audience: 'everyone', is_active: true, dismissible: true, popup_once: true,
  };
}

function draftFrom(a: Announcement): Draft {
  return {
    id: a.id, title: a.title, body: a.body, tone: a.tone, show_banner: a.show_banner,
    popup_style: a.popup_style, version_label: a.version_label ?? '', highlights: a.highlights.join('\n'),
    event_starts_at: toUtcInput(a.event_starts_at), event_ends_at: toUtcInput(a.event_ends_at),
    show_from: toUtcInput(a.show_from), show_until: toUtcInput(a.show_until), audience: a.audience,
    is_active: a.is_active, dismissible: a.dismissible, popup_once: a.popup_once,
  };
}

export function inputFrom(d: Draft): AnnouncementInput {
  const releaseNotes = d.popup_style === 'release_notes';
  return {
    title: d.title.trim(),
    body: d.body.trim(),
    tone: d.tone,
    show_banner: d.show_banner,
    popup_style: d.popup_style,
    version_label: releaseNotes ? d.version_label.trim() || null : null,
    highlights: releaseNotes ? d.highlights.split('\n').map((l) => l.trim()).filter(Boolean) : [],
    event_starts_at: fromUtcInput(d.event_starts_at),
    event_ends_at: fromUtcInput(d.event_ends_at),
    show_from: fromUtcInput(d.show_from),
    show_until: fromUtcInput(d.show_until),
    audience: d.audience,
    is_active: d.is_active,
    dismissible: d.dismissible,
    popup_once: d.popup_once,
  };
}

/** The first thing stopping a save, in words, or null. The server checks again. */
export function draftProblem(d: Draft): string | null {
  if (!d.title.trim()) return 'Give it a title.';
  if (!d.show_banner && d.popup_style === 'none') return 'Show it as a banner, a pop-up, or both.';
  if (d.popup_style === 'schedule' && !d.event_starts_at) return 'A schedule pop-up needs the time it starts.';
  if (d.event_starts_at && d.event_ends_at && d.event_ends_at <= d.event_starts_at) {
    return 'The event must end after it starts.';
  }
  if (d.show_from && d.show_until && d.show_until <= d.show_from) return 'Stop showing it after it starts.';
  return null;
}

function describe(a: Announcement): string {
  const parts: string[] = [];
  if (a.show_banner) parts.push('Banner');
  const style = POPUP_STYLES.find((s) => s.id === a.popup_style);
  if (a.popup_style !== 'none' && style) parts.push(`${style.label} pop-up`);
  if (a.audience === 'admins') parts.push('admins only');
  return parts.join(' · ');
}

@customElement('announcements-panel')
export class AnnouncementsPanel extends LightElement {
  private store = new StoreController(this, [myPermissions, catalogAvailable, catalogAvailabilityKnown]);

  @state() private items: Announcement[] = [];
  @state() private loading = false;
  @state() private error = '';
  @state() private draft: Draft | null = null;
  @state() private saving = false;
  @state() private saveError = '';
  @state() private busyId: string | null = null;
  /** The announcement whose "who" list is open, and that list once it arrives. */
  @state() private whoFor: string | null = null;
  @state() private who: AnnouncementReceipt[] | null = null;
  @state() private whoError = '';
  private requested = false;

  override willUpdate(): void {
    if (!this.requested && canDo(PERMISSION) && catalogAvailabilityKnown.value && catalogAvailable.value) {
      this.requested = true;
      void this.load();
    }
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = '';
    try {
      this.items = (await listAdminAnnouncements()).announcements;
    } catch (e) {
      this.error = userMessage(e);
    } finally {
      this.loading = false;
    }
  }

  private set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    if (!this.draft) return;
    this.draft = { ...this.draft, [key]: value };
    this.saveError = '';
  }

  private async save(): Promise<void> {
    const d = this.draft;
    if (!d || this.saving) return;
    const problem = draftProblem(d);
    if (problem) {
      this.saveError = problem;
      return;
    }
    this.saving = true;
    try {
      const input = inputFrom(d);
      const saved = d.id
        ? await updateAnnouncement(d.id, input)
        : await createAnnouncement(input);
      showToast('success', d.id ? 'Announcement saved' : 'Announcement published');
      this.draft = draftFrom(saved);
      await this.load();
      // The admin is a person too: their own header shows the change at once.
      void refreshAnnouncements();
    } catch (e) {
      this.saveError = userMessage(e);
    } finally {
      this.saving = false;
    }
  }

  private async toggle(a: Announcement, on: boolean): Promise<void> {
    this.busyId = a.id;
    try {
      const updated = await setAnnouncementActive(a.id, on);
      this.items = this.items.map((x) => (x.id === a.id ? { ...x, ...updated } : x));
      if (this.draft?.id === a.id) this.draft = { ...this.draft, is_active: on };
      void refreshAnnouncements();
    } catch (e) {
      showToast('error', userMessage(e));
    } finally {
      this.busyId = null;
    }
  }

  /** Everyone who closed or dismissed it sees it again - now, not on Save. */
  private async showAgain(): Promise<void> {
    const d = this.draft;
    if (!d?.id || this.busyId) return;
    const ok = await confirmAction({
      title: 'Show to everyone again?',
      message: `Everyone who closed "${d.title}" or dismissed its banner will see it again, `
        + 'the next time they load a page.',
      confirmLabel: 'Show again',
    });
    if (!ok) return;
    this.busyId = d.id;
    try {
      await showAnnouncementAgain(d.id);
      if (this.whoFor === d.id) this.who = [];
      showToast('success', 'Everyone will see it again');
      await this.load();
      void refreshAnnouncements();
    } catch (e) {
      showToast('error', userMessage(e));
    } finally {
      this.busyId = null;
    }
  }

  /** Open or close the list of who closed or dismissed it. Fetched each time it opens. */
  private async toggleWho(id: string): Promise<void> {
    if (this.whoFor === id) {
      this.whoFor = null;
      return;
    }
    this.whoFor = id;
    this.who = null;
    this.whoError = '';
    try {
      const { receipts } = await listAnnouncementReceipts(id);
      if (this.whoFor === id) this.who = receipts;
    } catch (e) {
      if (this.whoFor === id) this.whoError = userMessage(e);
    }
  }

  private renderWho(): TemplateResult {
    if (this.whoError) return html`<p class="ann-admin-note is-error" role="alert">${this.whoError}</p>`;
    if (!this.who) return html`<p class="ann-admin-note ann-who-note">Loading…</p>`;
    if (!this.who.length) return html`<p class="ann-admin-note ann-who-note">Nobody has closed or dismissed it yet.</p>`;
    const when = (iso: string) => html`<time datetime=${iso} title=${iso}>${timeAgo(iso)}</time>`;
    return html`<ul class="ann-who">
      ${repeat(this.who, (r) => r.user_id, (r) => html`<li>
        <span class="ann-who-person">
          <span class="ann-who-name">${r.display_name || r.email || 'Unknown person'}</span>
          ${r.display_name && r.email ? html`<span class="ann-who-email">${r.email}</span>` : nothing}
        </span>
        <span class="ann-who-acts">
          ${r.popup_seen_at ? html`<span>Closed the pop-up ${when(r.popup_seen_at)}</span>` : nothing}
          ${r.banner_dismissed_at ? html`<span>Dismissed the banner ${when(r.banner_dismissed_at)}</span>` : nothing}
        </span>
      </li>`)}
    </ul>`;
  }

  private renderShowAgain(d: Draft): TemplateResult | typeof nothing {
    const item = d.id ? this.items.find((a) => a.id === d.id) : undefined;
    if (!item) return nothing;
    const seen = item.seen_count ?? 0;
    const dismissed = item.dismissed_count ?? 0;
    const parts = [
      seen ? `${seen} closed the pop-up` : '',
      dismissed ? `${dismissed} dismissed the banner` : '',
    ].filter(Boolean);
    const open = this.whoFor === item.id;
    return html`<div class="ann-show-again">
      ${parts.length
        ? html`<button type="button" class="link-btn ann-who-toggle" aria-expanded=${open ? 'true' : 'false'}
            title=${open ? 'Hide who' : 'Show who'} @click=${() => void this.toggleWho(item.id)}>
            ${parts.join(' · ')} <span class="ann-who-caret" aria-hidden="true">${open ? '▴' : '▾'}</span></button>`
        : html`<span class="ann-sublabel">Nobody has closed or dismissed it yet</span>`}
      <button type="button" class="btn btn-secondary" ?disabled=${!parts.length || this.busyId === d.id}
        title="Everyone who closed or dismissed it sees it again"
        @click=${() => void this.showAgain()}>Show to everyone again</button>
    </div>
    ${open ? this.renderWho() : nothing}`;
  }

  private async deleteDraft(): Promise<void> {
    const d = this.draft;
    if (!d?.id) return;
    const ok = await confirmAction({
      title: 'Delete this announcement?',
      message: `"${d.title}" stops showing for everyone straight away${
        d.popup_style === 'release_notes' ? ", and leaves What's new" : ''}.`,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteAnnouncement(d.id);
      this.draft = null;
      showToast('success', 'Announcement deleted');
      await this.load();
      void refreshAnnouncements();
    } catch (e) {
      showToast('error', userMessage(e));
    }
  }

  private previewFull(): void {
    const d = this.draft;
    if (!d) return;
    const input = inputFrom(d);
    openAnnouncement(
      { ...input, title: input.title || 'Untitled announcement', id: d.id ?? 'preview', created_at: '', updated_at: '' },
      true,
    );
  }

  /* ── list ─────────────────────────────────────────────── */

  private renderList(): TemplateResult {
    return html`<section class="ann-admin-list" aria-label="Announcements">
      <div class="ann-admin-list-head">
        <button type="button" class="btn btn-primary" @click=${() => { this.draft = blankDraft(); this.saveError = ''; }}>
          New announcement
        </button>
      </div>
      <div class="ann-admin-rows" aria-busy=${this.loading}>
        ${this.error
          ? html`<p class="ann-admin-note is-error" role="alert">${this.error}</p>`
          : !this.items.length
            ? html`<p class="ann-admin-note">${this.loading ? 'Loading…' : 'No announcements yet. Write one and everyone sees it on every page.'}</p>`
            : repeat(this.items, (a) => a.id, (a) => {
                const status = announcementStatus(a);
                return html`<div class="ann-admin-row tone-${a.tone} ${this.draft?.id === a.id ? 'active' : ''}">
                  <button type="button" class="ann-admin-row-main" aria-current=${this.draft?.id === a.id ? 'true' : 'false'}
                    @click=${() => { this.draft = draftFrom(a); this.saveError = ''; }}>
                    <span class="ann-admin-row-top">
                      <span class="ann-admin-row-icon">${toneIcon(a.tone, 14)}</span>
                      <span class="ann-admin-row-title">${a.title}</span>
                    </span>
                    <span class="ann-admin-row-meta">
                      <span class="ann-status is-${status}">${STATUS_LABEL[status]}</span>
                      <span>${describe(a)}</span>
                    </span>
                    ${a.event_starts_at ? html`<span class="ann-admin-row-meta">${eventWindow(a)}</span>` : nothing}
                    ${a.seen_count || a.dismissed_count
                      ? html`<span class="ann-admin-row-meta">${a.seen_count ? `${a.seen_count} closed the pop-up` : ''}${
                          a.seen_count && a.dismissed_count ? ' · ' : ''}${a.dismissed_count ? `${a.dismissed_count} dismissed` : ''}</span>`
                      : nothing}
                  </button>
                  <input class="admin-switch" type="checkbox" role="switch" .checked=${a.is_active}
                    ?disabled=${this.busyId === a.id}
                    aria-label=${`${a.is_active ? 'Switch off' : 'Switch on'} ${a.title}`}
                    title=${a.is_active ? 'On - switch off to stop showing it' : 'Off - switch on to show it'}
                    @change=${(e: Event) => { const input = e.target as HTMLInputElement; const on = input.checked;
                      input.checked = a.is_active; void this.toggle(a, on); }} />
                </div>`;
              })}
      </div>
    </section>`;
  }

  /* ── editor ───────────────────────────────────────────── */

  private seg<T extends string>(value: T, options: ReadonlyArray<{ id: T; label: string }>, onPick: (v: T) => void,
    label: string): TemplateResult {
    return html`<div class="ann-seg" role="radiogroup" aria-label=${label}>
      ${options.map((o) => html`<button type="button" role="radio" aria-checked=${o.id === value ? 'true' : 'false'}
        class="ann-seg-btn ${o.id === value ? 'active' : ''}" @click=${() => onPick(o.id)}>${o.label}</button>`)}
    </div>`;
  }

  private renderEditor(): TemplateResult {
    const d = this.draft;
    if (!d) {
      return html`<section class="ann-admin-editor is-empty">
        <p class="ann-admin-note">Pick an announcement to change it, or write a new one.</p>
      </section>`;
    }
    const popup = d.popup_style !== 'none';
    return html`<section class="ann-admin-editor" aria-label=${d.id ? 'Edit announcement' : 'New announcement'}>
      <form class="ann-form" @submit=${(e: Event) => { e.preventDefault(); void this.save(); }}>
        <h2 class="ann-form-title">${d.id ? 'Edit announcement' : 'New announcement'}</h2>

        <label class="ann-field" for="ann-title-input">
          <span class="ann-label">Title</span>
          <input id="ann-title-input" type="text" maxlength="120" required .value=${d.title}
            placeholder="Scheduled maintenance on Saturday"
            @input=${(e: Event) => this.set('title', (e.target as HTMLInputElement).value)} />
        </label>
        <label class="ann-field" for="ann-body-input">
          <span class="ann-label">Message</span>
          <textarea id="ann-body-input" rows="4" maxlength="4000" .value=${d.body}
            placeholder="What is happening, what it affects, and what people should do."
            @input=${(e: Event) => this.set('body', (e.target as HTMLTextAreaElement).value)}></textarea>
        </label>

        <div class="ann-field">
          <span class="ann-label">Tone</span>
          ${this.seg(d.tone, TONES, (v) => this.set('tone', v), 'Tone')}
        </div>

        <div class="ann-field">
          <span class="ann-label">Show as</span>
          <label class="ann-check"><input type="checkbox" .checked=${d.show_banner}
            @change=${(e: Event) => this.set('show_banner', (e.target as HTMLInputElement).checked)} />
            Banner under the header, on every page</label>
          <div class="ann-style-grid" role="radiogroup" aria-label="Pop-up style">
            ${POPUP_STYLES.map((s) => html`<button type="button" role="radio" aria-checked=${d.popup_style === s.id ? 'true' : 'false'}
              class="ann-style ${d.popup_style === s.id ? 'active' : ''}" @click=${() => this.set('popup_style', s.id)}>
              <b>${s.label}</b><span>${s.hint}</span>
            </button>`)}
          </div>
        </div>

        ${d.popup_style === 'release_notes'
          ? html`<div class="ann-group">
              <label class="ann-field" for="ann-version-input">
                <span class="ann-label">Version</span>
                <input id="ann-version-input" type="text" maxlength="40" .value=${d.version_label} placeholder="1.9.0"
                  @input=${(e: Event) => this.set('version_label', (e.target as HTMLInputElement).value)} />
              </label>
              <label class="ann-field" for="ann-highlights-input">
                <span class="ann-label">What changed <span class="ann-hint">one per line, up to 12</span></span>
                <textarea id="ann-highlights-input" rows="4" .value=${d.highlights}
                  placeholder=${'Compare periods is open to everyone\nThe assistant can build period comparisons'}
                  @input=${(e: Event) => this.set('highlights', (e.target as HTMLTextAreaElement).value)}></textarea>
              </label>
            </div>`
          : nothing}

        <div class="ann-group">
          <span class="ann-label">${d.popup_style === 'schedule' ? 'When it happens (UTC)' : 'When it happens (UTC, optional)'}</span>
          <div class="ann-row2">
            <label class="ann-field" for="ann-event-start"><span class="ann-sublabel">Starts</span>
              <input id="ann-event-start" type="datetime-local" .value=${d.event_starts_at}
                @input=${(e: Event) => this.set('event_starts_at', (e.target as HTMLInputElement).value)} /></label>
            <label class="ann-field" for="ann-event-end"><span class="ann-sublabel">Ends</span>
              <input id="ann-event-end" type="datetime-local" .value=${d.event_ends_at}
                @input=${(e: Event) => this.set('event_ends_at', (e.target as HTMLInputElement).value)} /></label>
          </div>
        </div>

        <div class="ann-group">
          <span class="ann-label">Show it (UTC)</span>
          <div class="ann-row2">
            <label class="ann-field" for="ann-show-from"><span class="ann-sublabel">From - empty is now</span>
              <input id="ann-show-from" type="datetime-local" .value=${d.show_from}
                @input=${(e: Event) => this.set('show_from', (e.target as HTMLInputElement).value)} /></label>
            <label class="ann-field" for="ann-show-until"><span class="ann-sublabel">Until - empty is until switched off</span>
              <input id="ann-show-until" type="datetime-local" .value=${d.show_until}
                @input=${(e: Event) => this.set('show_until', (e.target as HTMLInputElement).value)} /></label>
          </div>
          <label class="ann-field" for="ann-audience"><span class="ann-sublabel">Who sees it</span>
            <select id="ann-audience" .value=${d.audience}
              @change=${(e: Event) => this.set('audience', (e.target as HTMLSelectElement).value as Draft['audience'])}>
              <option value="everyone">Everyone</option>
              <option value="admins">Administrators only</option>
            </select></label>
          ${d.show_banner ? html`<label class="ann-check"><input type="checkbox" .checked=${d.dismissible}
            @change=${(e: Event) => this.set('dismissible', (e.target as HTMLInputElement).checked)} />
            People can dismiss the banner</label>` : nothing}
          ${popup ? html`<label class="ann-check"><input type="checkbox" .checked=${d.popup_once}
            @change=${(e: Event) => this.set('popup_once', (e.target as HTMLInputElement).checked)} />
            Show the pop-up once per person <span class="ann-hint">otherwise once per visit</span></label>` : nothing}
          <label class="ann-check"><input type="checkbox" .checked=${d.is_active}
            @change=${(e: Event) => this.set('is_active', (e.target as HTMLInputElement).checked)} />
            On</label>
          ${this.renderShowAgain(d)}
        </div>

        ${this.saveError ? html`<p class="ann-admin-note is-error" role="alert">${this.saveError}</p>` : nothing}

        <div class="ann-form-foot">
          ${d.id ? html`<button type="button" class="btn btn-secondary ann-delete" @click=${() => void this.deleteDraft()}>Delete</button>` : nothing}
          <span class="ann-spacer"></span>
          <button type="button" class="btn btn-secondary" @click=${() => { this.draft = null; this.saveError = ''; }}>Cancel</button>
          <button type="submit" class="btn btn-primary" ?disabled=${this.saving}>
            ${this.saving ? 'Saving…' : d.id ? 'Save' : d.is_active ? 'Publish' : 'Save as off'}
          </button>
        </div>
      </form>
    </section>`;
  }

  /* ── preview ──────────────────────────────────────────── */

  private renderPreview(): TemplateResult | typeof nothing {
    const d = this.draft;
    if (!d) return nothing;
    const input = inputFrom(d);
    const shown = { ...input, title: input.title || 'Untitled announcement' };
    return html`<section class="ann-admin-preview" aria-label="Preview">
      <div class="ann-preview-head">
        <span class="ann-label">What people see</span>
        ${d.popup_style !== 'none'
          ? html`<button type="button" class="btn btn-secondary" @click=${() => this.previewFull()}>Preview full screen</button>`
          : nothing}
      </div>
      ${d.show_banner
        ? html`<div class="ann-preview-frame">
            <div class="ann-preview-header" aria-hidden="true"><span></span><span></span><span></span></div>
            <div class="ann-strip tone-${d.tone}">
              <span class="ann-strip-icon">${toneIcon(d.tone, 16)}</span>
              <span class="ann-strip-title">${shown.title}</span>
              ${shown.body ? html`<span class="ann-strip-body">${shown.body}</span>` : nothing}
              <span class="ann-strip-end">
                ${shown.event_starts_at ? html`<span class="ann-strip-when">${eventWindow(shown)}</span>` : nothing}
                ${shown.dismissible ? html`<span class="ann-strip-close" aria-hidden="true">×</span>` : nothing}
              </span>
            </div>
            <div class="ann-preview-page" aria-hidden="true"></div>
          </div>`
        : nothing}
      ${d.popup_style !== 'none'
        ? html`<div class="ann-preview-stage">
            ${renderPopupCard(shown, { onClose: () => this.previewFull(), inline: true })}
          </div>`
        : nothing}
    </section>`;
  }

  override render(): TemplateResult {
    if (!catalogAvailabilityKnown.value) return html`<div class="catalog-empty" role="status">Loading…</div>`;
    if (!catalogAvailable.value) {
      return html`<div class="catalog-empty"><p class="catalog-empty-title">Announcements need the metadata database</p>
        <p>Without it there is nowhere to keep them, and nobody is shown any.</p></div>`;
    }
    if (!canDo(PERMISSION)) {
      return html`<div class="catalog-empty"><p class="catalog-empty-title">You cannot publish announcements</p>
        <p>That takes <code>announcements:write</code>, which administrators hold.</p></div>`;
    }
    return html`<div class="ann-admin ${this.draft ? 'has-draft' : ''}">
      ${this.renderList()} ${this.renderEditor()} ${this.renderPreview()}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'announcements-panel': AnnouncementsPanel;
  }
}
